import React from 'react';
import {
  Card,
  Typography,
  Space,
  InputNumber,
  Button,
  Alert,
  Tag,
  Tooltip,
  message,
  Row,
  Col,
  Segmented,
  Dropdown
} from 'antd';
import {
  ReloadOutlined,
  CopyOutlined,
  FileTextOutlined,
  ClockCircleOutlined,
  StarOutlined,
  DownloadOutlined,
  SettingOutlined
} from '@ant-design/icons';

// Reports — просмотр отчёта с бэка.
//
// Ожидаемый API (на бэке):
//   GET /api/report?hours=24&star=2
// Ответ может быть:
//   1) { ok:true, text:"..." }
//   2) { ok:true, report:{...} }
//   3) plain text
//
// Авторизация: заголовок x-telegram-init-data

function getInitData() {
  try {
    return window.Telegram?.WebApp?.initData || '';
  } catch (_) {
    return '';
  }
}

async function fetchReport({ hours, star }) {
  const params = new URLSearchParams();
  params.set('hours', String(hours));
  if (star) params.set('star', String(star));

  const res = await fetch(`/api/report?${params.toString()}`, {
    method: 'GET',
    headers: {
      'x-telegram-init-data': getInitData()
    }
  });

  const ct = res.headers.get('content-type') || '';
  if (ct.includes('application/json')) {
    const data = await res.json().catch(() => ({}));
    if (!res.ok || data?.ok === false) {
      const msg = data?.error || data?.message || `HTTP ${res.status}`;
      const err = new Error(msg);
      err.status = res.status;
      err.data = data;
      throw err;
    }
    return data;
  }

  const text = await res.text();
  if (!res.ok) {
    const err = new Error(text || `HTTP ${res.status}`);
    err.status = res.status;
    throw err;
  }
  return { ok: true, text };
}

function normalizeResult(result) {
  const rawText =
    typeof result === 'string'
      ? result
      : result?.text
      ? String(result.text)
      : result?.report
      ? JSON.stringify(result.report, null, 2)
      : result
      ? JSON.stringify(result, null, 2)
      : '';

  if (rawText && typeof rawText === 'string') {
    const t = rawText.trim();
    if ((t.startsWith('{') && t.endsWith('}')) || (t.startsWith('[') && t.endsWith(']'))) {
      try {
        const parsed = JSON.parse(t);
        return JSON.stringify(parsed, null, 2);
      } catch (_) {
        return rawText;
      }
    }
  }
  return rawText;
}

function downloadText(filename, text) {
  try {
    const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  } catch (e) {
    message.error('Не получилось скачать файл');
  }
}

export default function Reports() {
  const [hours, setHours] = React.useState(24);
  const [star, setStar] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [result, setResult] = React.useState(null);
  const [lastLoadedAt, setLastLoadedAt] = React.useState(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const h = Number(hours);
      const safeHours = Number.isFinite(h) && h >= 1 && h <= 720 ? h : 24;
      const s = star == null ? null : Number(star);
      const safeStar = Number.isFinite(s) && s >= 1 && s <= 10 ? s : null;

      const data = await fetchReport({ hours: safeHours, star: safeStar });
      setResult(data);
      setLastLoadedAt(Date.now());
    } catch (e) {
      setError(e?.message || 'Ошибка загрузки отчёта');
      setResult(null);
    } finally {
      setLoading(false);
    }
  }, [hours, star]);

  React.useEffect(() => {
    load();
  }, [load]);

  const pretty = React.useMemo(() => normalizeResult(result), [result]);

  const canCopy = !!pretty && !loading;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(pretty);
      message.success('Скопировано');
    } catch (_) {
      message.error('Не получилось скопировать');
    }
  };

  const onPreset = (h) => setHours(h);

  const subtitle = (
    <Space size={8} wrap>
      <Tag icon={<ClockCircleOutlined />} bordered={false} style={styles.tag}>
        Период: {Number(hours) || 24}ч
      </Tag>
      <Tag icon={<StarOutlined />} bordered={false} style={styles.tag}>
        Звезда: {star ? `⭐${star}` : 'все'}
      </Tag>
      {lastLoadedAt ? (
        <Typography.Text type="secondary" style={{ fontSize: 12, opacity: 0.9 }}>
          Обновлено: {new Date(lastLoadedAt).toLocaleString('ru-RU')}
        </Typography.Text>
      ) : null}
    </Space>
  );

  const statusTag = error ? (
    <Tag color="error">ошибка</Tag>
  ) : loading ? (
    <Tag color="processing">загрузка</Tag>
  ) : (
    <Tag color="success">ok</Tag>
  );

  const formatTag = <Tag>{result?.report ? 'JSON' : 'Text'}</Tag>;

  const actionsMenu = {
    items: [
      {
        key: 'download',
        icon: <DownloadOutlined />,
        label: 'Скачать отчёт (.txt)',
        disabled: !pretty || loading
      },
      {
        key: 'copy',
        icon: <CopyOutlined />,
        label: 'Копировать',
        disabled: !canCopy
      }
    ],
    onClick: ({ key }) => {
      if (key === 'copy') return copy();
      if (key === 'download') {
        const s = star ? `star${star}` : 'all';
        const h = Number(hours) || 24;
        downloadText(`report_${s}_${h}h.txt`, pretty || '');
      }
    }
  };

  return (
    <div className="reports-page" style={styles.page}>
      <Space direction="vertical" size={12} style={{ width: '100%' }}>
        <div style={styles.header}>
          <Space size={10} align="center" wrap>
            <FileTextOutlined />
            <Typography.Title level={3} style={{ margin: 0 }}>
              Отчёты
            </Typography.Title>
            {statusTag}
            {formatTag}
          </Space>
          <Typography.Text type="secondary">
            Быстро смотрим статистику за период и по звёздам.
          </Typography.Text>
          <div style={{ marginTop: 8 }}>{subtitle}</div>
        </div>

        <Card
          bordered
          style={styles.card}
          title={
            <Space size={8} align="center">
              <SettingOutlined />
              <span>Параметры</span>
            </Space>
          }
          extra={
            <Space size={8} wrap>
              <Dropdown menu={actionsMenu} trigger={['click']}>
                <Button icon={<DownloadOutlined />} disabled={!pretty || loading}>
                  Действия
                </Button>
              </Dropdown>
              <Button type="primary" icon={<ReloadOutlined />} loading={loading} onClick={load}>
                Обновить
              </Button>
            </Space>
          }
        >
          <Space direction="vertical" size={12} style={{ width: '100%' }}>
            <Card size="small" style={styles.panel} bordered>
              <Row gutter={[12, 12]} align="middle">
                <Col xs={24} md={8}>
                  <div style={styles.field}>
                    <Typography.Text type="secondary">Период (часы)</Typography.Text>
                    <InputNumber
                      min={1}
                      max={720}
                      value={hours}
                      onChange={(v) => setHours(v ?? 24)}
                      style={{ width: '100%' }}
                    />
                    <Space size={6} wrap style={{ marginTop: 8 }}>
                      <Button size="small" onClick={() => onPreset(6)}>
                        6ч
                      </Button>
                      <Button size="small" onClick={() => onPreset(24)}>
                        24ч
                      </Button>
                      <Button size="small" onClick={() => onPreset(72)}>
                        72ч
                      </Button>
                      <Button size="small" onClick={() => onPreset(168)}>
                        7д
                      </Button>
                    </Space>
                  </div>
                </Col>

                <Col xs={24} md={10}>
                  <div style={styles.field}>
                    <Typography.Text type="secondary">Звезда</Typography.Text>
                    <Segmented
                      value={star == null ? 'all' : String(star)}
                      onChange={(v) => {
                        if (v === 'all') setStar(null);
                        else setStar(Number(v));
                      }}
                      options={[
                        { label: 'Все', value: 'all' },
                        { label: '⭐1', value: '1' },
                        { label: '⭐2', value: '2' },
                        { label: '⭐3', value: '3' },
                        { label: '⭐4', value: '4' },
                        { label: '⭐5', value: '5' },
                        { label: '⭐6', value: '6' }
                      ]}
                      block
                    />
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      Источник: <Typography.Text code>GET /api/report?hours=…&star=…</Typography.Text>
                    </Typography.Text>
                  </div>
                </Col>

                <Col xs={24} md={6}>
                  <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Space size={8} wrap>
                      <Tooltip title="Копировать отчёт">
                        <Button icon={<CopyOutlined />} onClick={copy} disabled={!canCopy} />
                      </Tooltip>
                      <Button
                        icon={<DownloadOutlined />}
                        disabled={!pretty || loading}
                        onClick={() => {
                          const s = star ? `star${star}` : 'all';
                          const h = Number(hours) || 24;
                          downloadText(`report_${s}_${h}h.txt`, pretty || '');
                        }}
                      >
                        Скачать
                      </Button>
                    </Space>
                  </div>
                </Col>
              </Row>
            </Card>

            {error ? (
              <Alert type="error" showIcon message="Ошибка" description={error} />
            ) : null}

            {!pretty && !loading && !error ? (
              <Alert
                type="info"
                showIcon
                message="Нет данных"
                description="Попробуйте увеличить период или выбрать другую звезду."
              />
            ) : null}

            <Card
              size="small"
              bordered
              style={styles.outputCard}
              title={
                <Space size={8} align="center">
                  <FileTextOutlined />
                  <span>Вывод отчёта</span>
                </Space>
              }
              extra={
                <Space size={8} wrap>
                  <Tag>{result?.report ? 'JSON' : 'Text'}</Tag>
                </Space>
              }
            >
              <pre style={styles.pre}>{pretty || (loading ? 'Загрузка…' : '')}</pre>
            </Card>
          </Space>
        </Card>
      </Space>
    </div>
  );
}

const styles = {
  page: {
    padding: 12,
    width: '100%',
    maxWidth: 980,
    margin: '0 auto'
  },

  header: {
    padding: 4
  },

  card: {
    borderRadius: 16
  },

  panel: {
    borderRadius: 16
  },

  outputCard: {
    borderRadius: 16
  },

  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6
  },

  pre: {
    margin: 0,
    padding: 12,
    borderRadius: 12,
    background: '#0b1220',
    color: '#e5e7eb',
    border: '1px solid rgba(0,0,0,0.12)',
    overflow: 'auto',
    maxHeight: 560,
    fontSize: 12,
    lineHeight: 1.45,
    fontFamily:
      'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace'
  }
};